import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { environment } from '../../environments/environment';

interface Schedule {
  id: string;
  name: string;
  resourceTypes?: ResourceType[];
  [key: string]: any;
}

interface ResourceType {
  id: string;
  name: string;
  [key: string]: any;
}

interface ResourceTypeSelection {
  resourceTypeId: string;
  required: boolean;
}

@Component({
  selector: 'app-schedule-dialog',
  templateUrl: './schedule-dialog.component.html',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    FormsModule,
    MatDialogModule,
    MatSelectModule,
    MatCheckboxModule,
    MatIconModule,
    CommonModule,
  ],
})
export class ScheduleDialogComponent implements OnInit {
  resourceTypes: ResourceType[] = [];
  selectedResourceTypes: string[] = [];
  requiredResourceTypes: string[] = []; // IDs of resource types that are required
  primaryResourceTypeId: string | null = null;
  loading = false;

  private apiBaseUrl = environment.apiBaseUrl;
  private organisationId: string = '';

  constructor(
    public dialogRef: MatDialogRef<ScheduleDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: Partial<Schedule>,
    private http: HttpClient
  ) {}

  ngOnInit() {
    // Get selected organisationId from localStorage
    const storedId = localStorage.getItem('selectedOrganisationId');
    if (storedId) {
      this.organisationId = storedId;
      
      // First fetch resource types
      this.fetchResourceTypes();
      
      // If editing, fetch existing resource type associations after resource types are loaded
      if (this.data.id) {
        // Increased delay to ensure resource types are fully loaded first
        setTimeout(() => {
          this.fetchScheduleResourceTypes();
        }, 300);
        // Set primaryResourceTypeId if present
        if (this.data['primaryResourceTypeId'] !== undefined && this.data['primaryResourceTypeId'] !== null) {
          this.primaryResourceTypeId = this.data['primaryResourceTypeId'].toString();
        }
      }
    }
  }

  fetchResourceTypes() {
    this.loading = true;
    this.http.get<ResourceType[]>(`${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes`)
      .subscribe({
        next: (data) => {
          this.resourceTypes = data;
          this.loading = false;
        },
        error: (err) => {
          console.error('Failed to load resource types', err);
          this.loading = false;
        }
      });
  }

  fetchScheduleResourceTypes() {
    if (!this.data.id) return;

    this.http.get<any[]>(`${this.apiBaseUrl}/schedules/${this.data.id}/resourcetypes`)
      .subscribe({
        next: (links) => {
          // Convert resourceTypeId to string to match mat-select value type
          this.selectedResourceTypes = links.map(link => link.resourceTypeId.toString());
          // Build list of required resource types
          this.requiredResourceTypes = links
            .filter(link => link.required !== false) // Default to true if undefined
            .map(link => link.resourceTypeId.toString());

          console.log('Loaded selected resource types:', this.selectedResourceTypes);
          console.log('Required resource types:', this.requiredResourceTypes);

          // Force change detection to update the UI
          setTimeout(() => {
            // This is a workaround to force Angular to re-evaluate the selected values
            this.selectedResourceTypes = [...this.selectedResourceTypes];
            this.requiredResourceTypes = [...this.requiredResourceTypes];
          }, 0);
        },
        error: (err) => {
          console.error('Failed to load schedule resource types', err);
        }
      });
  }


  onResourceTypesChange() {
    // When resource types selection changes, update required list
    // This ensures the two dropdowns stay in sync

    // Remove any required selections that are no longer in selectedResourceTypes
    this.requiredResourceTypes = this.requiredResourceTypes.filter(rtId =>
      this.selectedResourceTypes.includes(rtId)
    );

    // Auto-add newly selected resource types to required list (default behavior)
    // This means all newly added resource types default to "required"
    // Users can then uncheck them in the "Required Resource Types" dropdown to make them optional
    for (const rtId of this.selectedResourceTypes) {
      if (!this.requiredResourceTypes.includes(rtId)) {
        this.requiredResourceTypes.push(rtId);
      }
    }
  }

  getResourceTypeName(rtId: string): string {
    const resourceType = this.resourceTypes.find(rt => rt.id.toString() === rtId);
    return resourceType ? resourceType.name : '';
  }

  onSubmit() {
    if (this.data.name) {
      // Build resource type selections from the two lists
      // Each selection includes the resource type ID and whether it's required
      // required=true: MUST be scheduled in every session (scheduler will fail if impossible)
      // required=false: Optional, can be included when available but not mandatory
      const resourceTypeSelections = this.selectedResourceTypes.map(rtId => ({
        resourceTypeId: rtId,
        required: this.requiredResourceTypes.includes(rtId)
      }));

      // Include the selected resource types with their required status, primaryResourceTypeId, and currentVersionId in the result
      const result = {
        ...this.data,
        selectedResourceTypes: this.selectedResourceTypes, // Legacy field for backward compatibility
        resourceTypeSelections: resourceTypeSelections, // New field with required flag
        primaryResourceTypeId: this.primaryResourceTypeId
          ? parseInt(this.primaryResourceTypeId)
          : null,
        currentVersionId: this.data['currentVersionId'] // pass through for update
      };
      this.dialogRef.close(result);
    }
  }
}
